import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CopyIcon } from "@otrium/icons";
import { PromotionalBlockWrapper } from "./PromotionalBlock.styled";
import { Promotion, PromotionDisplayType } from "src/types/graphql.d";
import { Trans } from "@lingui/macro";
import { CheckCircleIcon } from "src/icons";
import classNames from "classnames";
import { DiscountRound } from "src/icons/DiscountRound";
import { usePromotionContext } from "src/hooks/usePromotionContext";
import { theme } from "src/theme";
import { useRouter } from "next/router";

type PromotionalBlockProps = {
  promotion: Promotion;
};

const now = new Date();

function PromotionalBlock({
  promotion,
}: PromotionalBlockProps): JSX.Element | null {
  const promoMessageRef = useRef<HTMLSpanElement>(null);
  const tapPromoMessageRef = useRef<HTMLSpanElement>(null);
  const [isCopied, setCopied] = useState(false);
  const { promotionContextState } = usePromotionContext();
  const router = useRouter();

  const copyCallback = useCallback(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 4000);
  }, []);

  const promoUrl = useMemo(() => {
    if (promotion.promo_display_type !== PromotionDisplayType.Default) {
      return undefined;
    }

    return promotion.promo_message_url;
  }, [promotion.promo_message_url, promotion.promo_display_type]);

  const handleClick = useCallback(() => {
    if (!promotion || (!promotion.promo_code && !promotion.promo_message_url)) {
      return;
    }

    if (promoUrl) {
      void router.push(promoUrl);

      return;
    }

    navigator?.clipboard
      ?.writeText(promotion.promo_code as string)
      .then(copyCallback)
      .catch(() => setCopied(false));
  }, [copyCallback, promotion, promoUrl, router]);

  const isPromotionActive = useMemo(
    () =>
      promotion.promo_end_date
        ? new Date(promotion.promo_end_date).getTime() > now.getTime()
        : false,
    [promotion.promo_end_date]
  );

  useEffect(() => {
    if (
      !promoMessageRef.current ||
      (!promotion.promo_code && !promotion.promo_message) ||
      !promotion.promo_code // Add this check to prevent empty string replacement
    ) {
      return;
    }

    promoMessageRef.current.innerHTML =
      promoMessageRef.current.innerHTML.replaceAll(
        promotion.promo_code,
        `<span class="promo-code">${promotion.promo_code}</span>`
      );

    if (tapPromoMessageRef.current) {
      tapPromoMessageRef.current.innerHTML =
        tapPromoMessageRef.current.innerHTML.replaceAll(
          promotion.promo_code,
          `<span class="promo-code">${promotion.promo_code}</span>`
        );
    }
  }, [promotion.promo_code, promotion.promo_message]);

  if (
    !promotion ||
    (!promotion.promo_code && !promotion.promo_message) ||
    promotion.promo_display_type === PromotionDisplayType.FlashSale
  ) {
    return null;
  }

  const hasPromotionEnded =
    !isPromotionActive || promotionContextState.hasPromotionEnded;

  const promocode = promotion.promo_code as string;

  const isActiveLightningDeal =
    promotion.promo_code &&
    !hasPromotionEnded &&
    promotion.promo_display_type === PromotionDisplayType.LightningDeal;

  return (
    <PromotionalBlockWrapper
      as={promoUrl || isActiveLightningDeal ? "button" : "div"}
      onClick={promoUrl || isActiveLightningDeal ? handleClick : undefined}
      className={classNames({
        ["success"]: isCopied,
        ["no-hover"]: !isActiveLightningDeal && (promoUrl || !promoUrl),
        ["no-click"]: !promoUrl && !isActiveLightningDeal,
      })}
      backgroundColor={
        hasPromotionEnded
          ? "#F9F9F9"
          : (promotion.promo_background_color as string)
      }
      highlightColor={
        hasPromotionEnded
          ? theme.colors.tone.anthraciteLight
          : (promotion.promo_highlight_color as string)
      }
      textColor={
        hasPromotionEnded
          ? theme.colors.tone.anthraciteLight
          : (promotion.promo_text_color as string)
      }
    >
      <div className="message">
        {promotion.promo_code && !hasPromotionEnded ? (
          <CopyIcon className="icon" />
        ) : (
          <DiscountRound className="icon" />
        )}
        <span ref={promoMessageRef} className="promo-message">
          {!hasPromotionEnded ? (
            /** Added dangerouslySetInnerHTML to parse <u>Selected Brands </u> */
            <span
              dangerouslySetInnerHTML={{
                __html: promotion.promo_message ?? "",
              }}
            />
          ) : (
            <Trans>Lightning Deal Expired</Trans>
          )}
        </span>
      </div>
      {promotion.promo_code && !hasPromotionEnded ? (
        <>
          <div className="hover-message">
            <DiscountRound className="icon" />
            <span ref={tapPromoMessageRef} className="promo-message">
              <Trans>Tap to copy your {promocode} discount code.</Trans>
            </span>
          </div>
          <div className="copy-success">
            <CheckCircleIcon className="icon-success" />
            <Trans>Coupon copied! Don’t forget to add it at checkout.</Trans>
          </div>
        </>
      ) : null}
    </PromotionalBlockWrapper>
  );
}

export { PromotionalBlock };
